import { DndContext, PointerSensor, TouchSensor, rectIntersection, useSensor, useSensors } from "@dnd-kit/core";
import { restrictToVerticalAxis, restrictToWindowEdges, snapCenterToCursor } from "@dnd-kit/modifiers";
import { SortableContext, arrayMove } from "@dnd-kit/sortable";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";

import { useCan } from "components";
import { ACL_ACTIVITIES, ACL_RESOURCES } from "models";
import {
  SortableItem,
  SortableOverlay
} from "modules/AppointmentDetails/components/Interventions/components/InterventionChecklists/components/PendingChecklists/components";
import "modules/AppointmentDetails/components/Interventions/components/InterventionChecklists/components/PendingChecklists/components/SortableList/SortableList.scss";
import { useReorderPendingChecklistMutation } from "modules/AppointmentDetails/components/Interventions/components/InterventionChecklists/components/PendingChecklists/hooks";
import { checkIfAannameChecklistsAreAtTheTop } from "modules/PendingChecklistsDashboard/util";

import type { DragEndEvent } from "@dnd-kit/core";
import type { Checklist } from "models";
import type { SortablePendingChecklists } from "modules/AppointmentDetails/components/Interventions/components/InterventionChecklists/components/PendingChecklists";
import type { Dispatch, SetStateAction } from "react";

type SortableListProps = {
  items: SortablePendingChecklists[];
  setItems: Dispatch<SetStateAction<SortablePendingChecklists[]>>;
  checklists?: Checklist[];
};

export const SortableList = ({ items, setItems, checklists }: SortableListProps) => {
  const { t } = useTranslation();
  const sensors = useSensors(useSensor(PointerSensor), useSensor(TouchSensor, { activationConstraint: { delay: 250, tolerance: 100 } }));

  const canReorderPendingChecklist = useCan(ACL_ACTIVITIES.Update, ACL_RESOURCES.InterventionPendingChecklist);
  const { mutateAsync: reorderPendingChecklistMutation } = useReorderPendingChecklistMutation();

  const handleOnDragEnd = async ({ active, over }: DragEndEvent) => {
    if (!over || active.id === over.id) return;

    try {
      const newIndex = items.findIndex(item => item.id === over.id);
      const oldIndex = items.findIndex(item => item.id === active.id);
      const sortedItems = arrayMove(items, oldIndex, newIndex);

      if (checklists && !checkIfAannameChecklistsAreAtTheTop(sortedItems, checklists)) {
        toast.error(t("v8_invalid_order_aanname_checklists_must_be_at_the_top_of_the_list", "Invalid order: Aanname checklists must be at the top of the list"));
        return;
      }

      setItems(sortedItems);
      await reorderPendingChecklistMutation({ items, oldIndex, newIndex });
    } catch (error) {
      setItems(items);
      console.error(error);
    }
  };

  const handleRemoveItem = (id: number) => {
    setItems(prev => prev.filter(checklist => checklist.checklist_id !== id).map((c, index) => ({ ...c, ordering: index + 1 })));
  };

  return (
    <div className="PendingChecklistsSortableList">
      <DndContext
        sensors={sensors}
        collisionDetection={rectIntersection}
        onDragEnd={handleOnDragEnd}
        modifiers={[restrictToVerticalAxis, restrictToWindowEdges, snapCenterToCursor]}
      >
        <>
          <SortableContext items={items} disabled={!canReorderPendingChecklist}>
            <ul className="check-container">
              {items.map(item => (
                <SortableItem key={`pending-checklist-${item.checklist_id}`} item={item} handleRemoveItem={handleRemoveItem} />
              ))}
            </ul>
          </SortableContext>
          <SortableOverlay />
        </>
      </DndContext>
    </div>
  );
};
